import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Modal,
  Radio,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  message,
} from 'antd';
import { WalletOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { api } from '../api/client';
import { useAuthStore } from '../store/auth';

interface RechargeSku {
  code: string;
  amount: string;
  tokenAmount: number;
}

interface RechargeOrder {
  orderNo: string;
  tenantSlug?: string | null;
  tenantName?: string | null;
  skuCode: string;
  amount: string;
  tokenAmount: number;
  status: 'pending' | 'paid' | 'expired' | 'closed';
  createdAt: string | null;
  paidAt: string | null;
}

interface PaymentStatus {
  alipayConfigured: boolean;
  manualPayEnabled: boolean;
  sandbox: boolean;
}

const STATUS_TAG: Record<RechargeOrder['status'], { color: string; label: string }> = {
  pending: { color: 'warning', label: '待支付' },
  paid: { color: 'success', label: '已到账' },
  expired: { color: 'default', label: '已过期' },
  closed: { color: 'default', label: '已关闭' },
};

function formatToken(n: number | string) {
  const v = typeof n === 'string' ? Number(n) : n;
  if (!Number.isFinite(v)) return String(n);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(v % 1_000_000 === 0 ? 0 : 1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(v % 1_000 === 0 ? 0 : 1)}K`;
  return String(v);
}

function postFormToAlipay(link: { payFormHtml?: string; gateway?: string; orderString?: string }) {
  if (link.payFormHtml) {
    const host = document.createElement('div');
    host.innerHTML = link.payFormHtml;
    document.body.appendChild(host);
    const form = host.querySelector('form');
    form?.submit();
    host.remove();
    return;
  }
  const gateway = link.gateway ?? '';
  const orderString = link.orderString ?? '';
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = gateway;
  form.target = '_self';
  orderString.split('&').forEach((pair) => {
    const eq = pair.indexOf('=');
    if (eq < 0) return;
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = decodeURIComponent(pair.slice(0, eq));
    input.value = decodeURIComponent(pair.slice(eq + 1).replace(/\+/g, '%20'));
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
}

export function BillingPage() {
  const profile = useAuthStore((s) => s.profile);
  const isAdmin =
    profile?.roles.includes('ADMIN') || profile?.roles.includes('SUPER_ADMIN');
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [selectedSku, setSelectedSku] = useState('');
  const [polling, setPolling] = useState(false);

  const { data: paymentStatus } = useQuery<PaymentStatus>({
    queryKey: ['payment-status'],
    queryFn: () => api.get('/payment/status'),
  });

  const { data: balance, refetch: refetchBalance } = useQuery<{
    tokenBalance: string;
    tenantSlug?: string;
    tenantName?: string;
  }>({
    queryKey: ['payment-balance'],
    queryFn: () => api.get('/payment/balance'),
    enabled: isAdmin,
  });

  const { data: skus = [] } = useQuery<RechargeSku[]>({
    queryKey: ['payment-skus'],
    queryFn: () => api.get('/payment/skus'),
    enabled: isAdmin,
  });

  const { data: orders = [], refetch: refetchOrders } = useQuery<RechargeOrder[]>({
    queryKey: ['payment-orders'],
    queryFn: () => api.get('/payment/orders', { params: { limit: 10 } }),
    enabled: isAdmin,
  });

  useEffect(() => {
    if (!skus.length) return;
    const test = skus.find((s) => s.code === 'token_test_1f');
    setSelectedSku((prev) => prev || test?.code || skus[0]?.code || '');
  }, [skus]);

  const confirmLabel = useMemo(() => {
    if (paymentStatus?.alipayConfigured) return '确认充值';
    if (paymentStatus?.manualPayEnabled) return '模拟支付（沙箱）';
    return '确认充值';
  }, [paymentStatus]);

  const pollRechargeResult = async (orderNo: string) => {
    setPolling(true);
    try {
      for (let i = 0; i < 20; i++) {
        const res: {
          status: string;
          reconcileResult: string;
        } = await api.get(`/payment/alipay/query/${orderNo}`);
        if (res.status === 'paid') {
          message.success('充值成功，Token 已到账');
          await Promise.all([
            refetchBalance(),
            refetchOrders(),
            queryClient.invalidateQueries({ queryKey: ['payment-orders'] }),
          ]);
          return;
        }
        await new Promise((r) => setTimeout(r, 3000));
      }
      message.warning('支付结果确认中，请稍后刷新页面查看余额');
    } finally {
      setPolling(false);
      const next = new URLSearchParams(searchParams);
      next.delete('out_trade_no');
      setSearchParams(next, { replace: true });
    }
  };

  useEffect(() => {
    const orderNo = searchParams.get('out_trade_no');
    if (orderNo?.startsWith('TG') && isAdmin) {
      void pollRechargeResult(orderNo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('out_trade_no'), isAdmin]);

  const rechargeMutation = useMutation({
    mutationFn: async (skuCode: string) => {
      const order: RechargeOrder = await api.post('/payment/orders', { skuCode });
      if (!paymentStatus?.alipayConfigured && paymentStatus?.manualPayEnabled) {
        await api.post(`/payment/orders/${order.orderNo}/manual-pay`);
        return { mode: 'manual' as const, order };
      }
      const link: { payFormHtml?: string; gateway: string; orderString: string } = await api.post(
        `/payment/alipay/pay/${order.orderNo}`,
      );
      postFormToAlipay(link);
      return { mode: 'alipay' as const, order };
    },
    onSuccess: async (result) => {
      if (result.mode === 'manual') {
        message.success('沙箱模拟支付成功');
        await refetchBalance();
        await refetchOrders();
        setRechargeOpen(false);
      }
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message || err?.message || '充值失败';
      message.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    },
  });

  if (!isAdmin) {
    return (
      <div>
        <div className="tg-page-title">账户与充值</div>
        <Alert type="warning" showIcon message="仅管理员可访问此页面" />
      </div>
    );
  }

  return (
    <div>
      <div className="tg-page-title">账户与充值</div>

      {paymentStatus && !paymentStatus.alipayConfigured && !paymentStatus.manualPayEnabled && (
        <Alert
          style={{ marginBottom: 16 }}
          type="info"
          showIcon
          message="支付通道未配置"
          description="服务器尚未配置 ALIPAY_* 环境变量。联调时可开启 ALLOW_MANUAL_PAY=true 使用模拟支付。"
        />
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <Card>
            <Statistic
              title={
                balance?.tenantSlug
                  ? `租户 Token 余额（${balance.tenantSlug}${balance.tenantName ? ` · ${balance.tenantName}` : ''}）`
                  : '租户 Token 余额'
              }
              value={balance?.tokenBalance ?? '0'}
              formatter={(v) => formatToken(String(v))}
              prefix={<ThunderboltOutlined />}
              suffix={<span style={{ fontSize: 14, color: '#888' }}> tokens</span>}
            />
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card>
            <Space direction="vertical">
              <div style={{ color: '#666' }}>为租户充值 AI Token 额度，支付成功后自动入账。</div>
              <Button
                type="primary"
                icon={<WalletOutlined />}
                onClick={() => setRechargeOpen(true)}
                loading={polling}
              >
                立即充值
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>

      <Card title="最近充值订单">
        <Table
          rowKey="orderNo"
          dataSource={orders}
          pagination={false}
          columns={[
            { title: '订单号', dataIndex: 'orderNo', width: 220 },
            {
              title: '租户',
              key: 'tenant',
              width: 160,
              render: (_, r) =>
                r.tenantSlug ? (
                  <span>
                    <strong>{r.tenantSlug}</strong>
                    {r.tenantName ? (
                      <span style={{ color: '#999', marginLeft: 6 }}>{r.tenantName}</span>
                    ) : null}
                  </span>
                ) : (
                  '-'
                ),
            },
            {
              title: '套餐',
              dataIndex: 'skuCode',
              width: 120,
              render: (v, r) => (
                <span>
                  {v}{' '}
                  <span style={{ color: '#999' }}>({formatToken(r.tokenAmount)})</span>
                </span>
              ),
            },
            {
              title: '金额',
              dataIndex: 'amount',
              width: 90,
              render: (v) => `¥${v}`,
            },
            {
              title: '状态',
              dataIndex: 'status',
              width: 100,
              render: (v: RechargeOrder['status']) => (
                <Tag color={STATUS_TAG[v]?.color}>{STATUS_TAG[v]?.label ?? v}</Tag>
              ),
            },
            {
              title: '创建时间',
              dataIndex: 'createdAt',
              width: 170,
              render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-'),
            },
            {
              title: '到账时间',
              dataIndex: 'paidAt',
              width: 170,
              render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-'),
            },
          ]}
        />
      </Card>

      <Modal
        title="Token 充值"
        open={rechargeOpen}
        onCancel={() => setRechargeOpen(false)}
        onOk={() => {
          if (!selectedSku) {
            message.warning('请选择充值档位');
            return;
          }
          rechargeMutation.mutate(selectedSku);
        }}
        confirmLoading={rechargeMutation.isPending}
        okText={confirmLabel}
      >
        <Radio.Group
          style={{ width: '100%' }}
          value={selectedSku}
          onChange={(e) => setSelectedSku(e.target.value)}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            {skus.map((sku) => (
              <Radio key={sku.code} value={sku.code} style={{ width: '100%' }}>
                <Space>
                  <strong>¥{sku.amount}</strong>
                  <span style={{ color: '#666' }}>
                    → {formatToken(sku.tokenAmount)} tokens
                  </span>
                  {sku.code === 'token_test_1f' && <Tag>联调</Tag>}
                </Space>
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      </Modal>
    </div>
  );
}
